import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { matchClassRoster, saveRosterAliases } from '../../../api/classRosterApi';
import { trackClassRosterEvent } from '../../../utils/eventTracker';
import { getPreferredRoster } from '../../../utils/preferredRoster';
import { planOrderFill, suggestNextSid } from '../../../utils/rosterOrderFill';
import { isPlaceholderStudentName } from '../../../utils/studentNamePolicy';
import './ClassRosterCard.css';

const CONFIRMED = new Set(['name', 'alias', 'file_name']);
// 서버 자동 찾기 기준(ROSTER_MATCH_MIN_OVERLAP)과 같다. 학급 관리에서 고른 명단도 이만큼 겹칠 때만 먼저 쓴다.
const PREFERRED_MIN_OVERLAP = 0.7;

const toNames = (students) => students.map(student => String(student.name || '').trim());
// 이름을 못 읽은 카드는 서버가 파일명·본문 머리에서 명단 이름을 찾는다(명단은 AI에 보내지 않는다).
const CLUE_HEAD_CHARS = 200;
const toClues = (students) => {
  const clues = students.map(student => ({
    file_name: String(student.source_file_name || '').slice(0, 200),
    head: String(student.existing_content || student.content || '').slice(0, CLUE_HEAD_CHARS),
  }));
  return clues.some(clue => clue.file_name || clue.head) ? clues : null;
};

// 학급 관리에서 고른 명단을 먼저 맞춰 보고, 다른 반 파일이면 자동 찾기로 넘어간다. 1·2학기 같은 반 명단 중 고른 쪽을 쓴다.
export const matchWithPreferredRoster = async (names, clues = null) => {
  const preferred = getPreferredRoster();
  if (preferred) {
    const picked = await matchClassRoster(names, preferred.id, clues).catch(() => null);
    if (picked?.roster && Number(picked.overlap || 0) >= PREFERRED_MIN_OVERLAP) return { result: picked, source: 'preferred' };
  }
  return { result: await matchClassRoster(names, null, clues), source: 'auto' };
};

export const buildRosterDefaultLabel = (options = {}, now = new Date()) => {
  const category = String(options.category || '').replace(/_/g, ' ').trim();
  return `${now.getMonth() + 1}월 ${category ? `${category} ` : ''}명단`;
};

export const rosterStudentKey = (student, index) => student?.id || `index_${index}`;

// 카드가 처음 본 학생 기준으로 맞춘 결과를, 그 사이 지우거나 추가해도 같은 학생에 붙인다.
// 학생별 기록에서 넘어온 학생은 처음부터 roster_sid를 들고 온다. 카드가 연결한 적이 없으면 그대로 둔다.
export const useClassRosterLink = (setStudents) => {
  const [rosterId, setRosterId] = useState(null);
  const linkedRef = useRef(false);
  const onApply = useCallback((updates) => {
    const byKey = new Map(updates.map(update => [update.key, update]));
    setStudents(previous => previous.map((student, index) => {
      const update = byKey.get(rosterStudentKey(student, index));
      if (!update) return student;
      return {
        ...student,
        roster_sid: update.roster_sid,
        ...(update.name ? { name: update.name, _recognitionEdited: true } : {}),
      };
    }));
  }, [setStudents]);
  const onRosterChange = useCallback((nextRosterId) => {
    setRosterId(nextRosterId);
    if (nextRosterId) {
      linkedRef.current = true;
    } else if (linkedRef.current) {
      linkedRef.current = false;
      setStudents(previous => previous.map(({ roster_sid: _unused, ...student }) => student));
    }
  }, [setStudents]);
  return { rosterId, onApply, onRosterChange };
};

// 올린 학생 이름을 '우리 반' 명단과 맞춘다. 명단이 없으면 저장을 권하고, 있으면 다른 것만 보여 준다.
// 못 맞춘 카드 옆에 보여 줄 단서: 파일 이름, 없으면 본문 첫 줄. 화면에만 쓰고 어디에도 보내지 않는다.
const cardClue = (student) => {
  const file = String(student?.source_file_name || '').trim();
  if (file) return file;
  const firstLine = String(student?.existing_content || student?.content || '')
    .split('\n').map(line => line.trim()).find(Boolean) || '';
  return firstLine.length > 40 ? `${firstLine.slice(0, 40)}…` : firstLine;
};

const matchCounts = (matches = []) => ({
  exact_count: matches.filter(match => match.how === 'name').length,
  alias_count: matches.filter(match => match.how === 'alias').length,
  similar_count: matches.filter(match => match.how === 'similar').length,
  file_name_count: matches.filter(match => match.how === 'file_name').length,
  clue_count: matches.filter(match => !match.sid && match.clue_sid).length,
  unmatched_count: matches.filter(match => !match.how).length,
});

const ClassRosterCard = ({ students, createdFrom = 'batch', onApply, onRosterChange }) => {
  const [state, setState] = useState('loading');
  const [suggestion, setSuggestion] = useState(null);
  const [isPickingRoster, setIsPickingRoster] = useState(false);
  const [resolved, setResolved] = useState({});
  const [matchedVia, setMatchedVia] = useState('auto');
  const [focusIndex, setFocusIndex] = useState(null);
  const [fillPreview, setFillPreview] = useState(null);
  const [lastFill, setLastFill] = useState(null);
  const matchedStudentsRef = useRef(students);
  const autoMatchStartedRef = useRef(false);
  const hasEnoughNames = toNames(students).filter(Boolean).length >= 2;
  const keyAt = (index) => rosterStudentKey(matchedStudentsRef.current[index], index);

  const applySuggestion = useCallback((result, { source, elapsedMs }) => {
    setSuggestion(result);
    setMatchedVia(source);
    setResolved({});
    setFocusIndex(null);
    setFillPreview(null);
    setLastFill(null);
    if (!result?.roster) {
      onRosterChange?.(null);
      // 저장은 결과 화면(RosterResultCard)에서 묻는다. 여기서는 저장한 명단이 있을 때만 고르게 한다.
      if (!(result?.rosters || []).length) {
        setState('hidden');
        return;
      }
      setState('none');
      trackClassRosterEvent('class_roster_save_prompt_shown', {
        surface: createdFrom,
        student_count: toNames(matchedStudentsRef.current).filter(Boolean).length,
        roster_count: (result?.rosters || []).length,
        has_saved_rosters: (result?.rosters || []).length > 0,
        elapsed_ms: elapsedMs,
      });
      return;
    }
    onRosterChange?.(result.roster.id);
    onApply?.(result.matches
      .filter(match => CONFIRMED.has(match.how))
      .map(match => ({
        key: keyAt(match.index),
        roster_sid: match.sid,
        ...((match.how === 'alias' || match.how === 'file_name') && match.roster_name ? { name: match.roster_name } : {}),
      })));
    setState('matched');
    trackClassRosterEvent('class_roster_matched', {
      source,
      surface: createdFrom,
      total: result.total,
      confirmed: result.matches.filter(match => CONFIRMED.has(match.how)).length,
      ...matchCounts(result.matches),
      overlap_pct: Number(result.overlap || 0) * 100,
      roster_count: (result.rosters || []).length,
      elapsed_ms: elapsedMs,
      roster_id: result.roster.id,
    });
  }, [createdFrom, onApply, onRosterChange]);

  // 학생이 2명이 되는 순간 자동으로 맞춘다(처음엔 1명이었다가 나중에 추가해도 카드가 나온다).
  // 1명 이하로 줄면 명단 연결을 풀고, 다시 2명이 되면 그때 학생들로 새로 맞춘다.
  useEffect(() => {
    if (!hasEnoughNames) {
      autoMatchStartedRef.current = false;
      onRosterChange?.(null);
      setSuggestion(null);
      setState('hidden');
      return undefined;
    }
    if (autoMatchStartedRef.current) return undefined;
    autoMatchStartedRef.current = true;
    matchedStudentsRef.current = students;
    let cancelled = false;
    let settled = false;
    const startedAt = Date.now();
    Promise.resolve()
      .then(() => matchWithPreferredRoster(toNames(students), toClues(students)))
      .then(({ result, source }) => {
        settled = true;
        if (!cancelled) applySuggestion(result, { source, elapsedMs: Date.now() - startedAt });
      })
      .catch(() => {
        settled = true;
        if (cancelled) return;
        onRosterChange?.(null);
        setState('hidden');
      });
    return () => {
      cancelled = true;
      if (!settled) autoMatchStartedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasEnoughNames]);

  const roster = suggestion?.roster;
  const rosterStudents = useMemo(() => roster?.students || [], [roster]);
  // 맞춘 뒤 목록에서 지운 학생은 확인할 대상이 아니다(연결해도 반영될 학생이 없다).
  const liveKeys = useMemo(() => new Set((students || []).map((student, index) => rosterStudentKey(student, index))), [students]);
  const exceptions = useMemo(
    () => (suggestion?.matches || []).filter(match => !CONFIRMED.has(match.how)
      && !Object.prototype.hasOwnProperty.call(resolved, match.index)
      && liveKeys.has(keyAt(match.index))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [liveKeys, resolved, suggestion]
  );
  const usedSids = useMemo(() => new Set([
    ...(suggestion?.matches || []).filter(match => CONFIRMED.has(match.how)).map(match => match.sid),
    ...Object.values(resolved).filter(Boolean),
  ]), [resolved, suggestion]);

  const links = useMemo(() => {
    const out = {};
    (suggestion?.matches || []).forEach(match => {
      if (CONFIRMED.has(match.how)) out[match.index] = match.sid;
    });
    Object.entries(resolved).forEach(([index, sid]) => {
      if (sid) out[index] = sid;
    });
    return out;
  }, [resolved, suggestion]);
  const unmatched = exceptions.filter(match => match.how !== 'similar');
  const similarRows = exceptions.filter(match => match.how === 'similar');
  const current = unmatched.find(match => match.index === focusIndex) || unmatched[0] || null;
  const clueSid = current?.clue_sid && !usedSids.has(current.clue_sid) ? current.clue_sid : null;
  const recommendedSid = current
    ? clueSid || suggestNextSid({ index: current.index, links, rosterStudents, numberHint: current.number_hint, usedSids })
    : null;
  const fillPlan = useMemo(() => planOrderFill({
    openIndexes: unmatched.map(match => match.index),
    links,
    rosterStudents,
  }), [links, rosterStudents, unmatched]);

  const pickStudent = async (match, sid, via = 'select') => {
    const picked = rosterStudents.find(student => student.sid === sid);
    if (!picked) return;
    setResolved(previous => ({ ...previous, [match.index]: sid }));
    setFocusIndex(null);
    onApply?.([{ key: keyAt(match.index), roster_sid: sid, name: picked.name }]);
    trackClassRosterEvent('class_roster_name_fixed', {
      how: match.how || 'none', via, surface: createdFrom, roster_id: roster.id,
    });
    if (match.name && match.name !== picked.name && !isPlaceholderStudentName(match.name)) {
      try {
        await saveRosterAliases(roster.id, [{ sid, alias: match.name }]);
      } catch (_error) {
        // 별칭 저장이 실패해도 이번 생성의 이름 맞추기는 그대로 둔다.
      }
    }
  };

  const keepAsIs = (match) => {
    setResolved(previous => ({ ...previous, [match.index]: '' }));
    setFocusIndex(null);
    trackClassRosterEvent('class_roster_name_kept', { how: match.how || 'none', surface: createdFrom, roster_id: roster?.id });
  };

  const openFillPreview = (kind) => {
    const items = kind === 'verified' ? fillPlan.verified : fillPlan.guessed;
    if (!items.length) return;
    setFillPreview({ kind, items });
    trackClassRosterEvent('class_roster_order_fill_preview', {
      kind, count: items.length, anchor_count: fillPlan.anchorCount, surface: createdFrom, roster_id: roster?.id,
    });
  };

  const applyFill = () => {
    if (!fillPreview) return;
    const byIndex = new Map((suggestion?.matches || []).map(match => [match.index, match]));
    const studentBySid = new Map(rosterStudents.map(student => [student.sid, student]));
    const items = fillPreview.items.filter(item => studentBySid.has(item.sid));
    setResolved(previous => ({ ...previous, ...Object.fromEntries(items.map(item => [item.index, item.sid])) }));
    onApply?.(items.map(item => ({ key: keyAt(item.index), roster_sid: item.sid, name: studentBySid.get(item.sid).name })));
    setLastFill(items.map(item => ({ index: item.index, name: byIndex.get(item.index)?.name || '' })));
    setFillPreview(null);
    setFocusIndex(null);
    trackClassRosterEvent('class_roster_order_filled', {
      kind: fillPreview.kind, count: items.length, anchor_count: fillPlan.anchorCount, surface: createdFrom, roster_id: roster?.id,
    });
  };

  const undoFill = () => {
    if (!lastFill) return;
    setResolved(previous => {
      const next = { ...previous };
      lastFill.forEach(item => { delete next[item.index]; });
      return next;
    });
    onApply?.(lastFill.map(item => ({ key: keyAt(item.index), roster_sid: undefined, name: item.name })));
    trackClassRosterEvent('class_roster_order_fill_undone', { count: lastFill.length, surface: createdFrom, roster_id: roster?.id });
    setLastFill(null);
  };

  const switchRoster = async (rosterId) => {
    setIsPickingRoster(false);
    try {
      matchedStudentsRef.current = students;
      applySuggestion(await matchClassRoster(toNames(students), rosterId, toClues(students)), { source: 'switch' });
    } catch (_error) {
      onRosterChange?.(null);
      setState('hidden');
    }
  };

  if (state === 'loading' || state === 'hidden') return null;

  if (state === 'none') {
    return (
      <section className="class-roster-card is-suggest" data-testid="class-roster-card">
        <div className="class-roster-card-saved">
          <span>저장한 명단으로 맞추기</span>
          <div className="class-roster-card-rosters">
            {(suggestion?.rosters || []).map(item => (
              <button key={item.id} type="button" onClick={() => switchRoster(item.id)}>
                {item.label} · {item.student_count}명
              </button>
            ))}
          </div>
        </div>
      </section>
    );
  }

  const otherRosters = (suggestion?.rosters || []).filter(item => item.id !== roster?.id);
  const confirmedCount = (suggestion?.total || 0) - exceptions.length;

  return (
    <section className="class-roster-card" data-testid="class-roster-card">
      <div className="class-roster-card-head">
        <span>
          {matchedVia === 'auto'
            ? `지난번 '${roster.label}' 명단으로 맞췄어요`
            : `'${roster.label}' 명단으로 맞췄어요`}
        </span>
        {otherRosters.length > 0 && (
          <button type="button" className="class-roster-card-link" onClick={() => setIsPickingRoster(value => !value)}>
            바꾸기
          </button>
        )}
        <a className="class-roster-card-link" href="/classes" target="_blank" rel="noopener noreferrer">
          학급 관리에서 고치기
        </a>
      </div>
      {isPickingRoster && (
        <div className="class-roster-card-rosters">
          {otherRosters.map(item => (
            <button key={item.id} type="button" onClick={() => switchRoster(item.id)}>
              {item.label} · {item.student_count}명
            </button>
          ))}
        </div>
      )}
      <strong className="class-roster-card-summary">
        {exceptions.length === 0
          ? `${suggestion.total}명 모두 맞췄어요`
          : `${suggestion.total}명 중 ${confirmedCount}명을 맞췄어요. ${exceptions.length}명만 확인해 주세요`}
      </strong>
      {similarRows.length > 0 && (
        <ul className="class-roster-card-exceptions">
          {similarRows.map(match => (
            <li key={match.index}>
              <span className="class-roster-card-name">{match.name || `학생 ${match.index + 1}`}</span>
              <button type="button" onClick={() => pickStudent(match, match.sid)}>
                {match.roster_name}(으)로 바꾸기
              </button>
              <button type="button" className="is-quiet" onClick={() => keepAsIs(match)}>그대로 두기</button>
            </li>
          ))}
        </ul>
      )}
      {lastFill && (
        <div className="class-roster-card-filled">
          <span>{`순서로 ${lastFill.length}명을 연결했어요`}</span>
          <button type="button" className="is-quiet" onClick={undoFill}>순서 연결 되돌리기</button>
        </div>
      )}
      {fillPreview && (
        <div className="class-roster-card-preview" data-testid="class-roster-fill-preview">
          {fillPreview.kind === 'guessed' && (
            <p className="class-roster-card-warning">
              올린 순서가 명단 번호 순서와 다를 수 있어요. 아래 짝이 맞는지 확인해 주세요.
            </p>
          )}
          <ul>
            {fillPreview.items.map(item => {
              const match = (suggestion?.matches || []).find(m => m.index === item.index);
              const student = rosterStudents.find(s => s.sid === item.sid);
              return (
                <li key={item.index}>
                  <span>{match?.name || `학생 ${item.index + 1}`}</span>
                  <span aria-hidden="true">→</span>
                  <strong>{student?.number}. {student?.name}</strong>
                </li>
              );
            })}
          </ul>
          <div className="class-roster-card-actions">
            <button type="button" onClick={applyFill}>{`${fillPreview.items.length}명 이대로 연결하기`}</button>
            <button type="button" className="is-quiet" onClick={() => setFillPreview(null)}>취소</button>
          </div>
        </div>
      )}
      {current && !fillPreview && (
        <div className="class-roster-card-quick" data-testid="class-roster-quick">
          <div className="class-roster-card-quick-head">
            <span className="class-roster-card-name">{current.name || `학생 ${current.index + 1}`}</span>
            {cardClue(matchedStudentsRef.current[current.index]) && (
              <span className="class-roster-card-clue">{cardClue(matchedStudentsRef.current[current.index])}</span>
            )}
            <span className="class-roster-card-remaining">{`남은 학생 ${unmatched.length}명`}</span>
          </div>
          <div className="class-roster-card-chips" role="group" aria-label={`${current.name || '학생'} 명단에서 고르기`}>
            {rosterStudents.filter(student => !usedSids.has(student.sid)).map(student => (
              <button
                key={student.sid}
                type="button"
                className={student.sid === recommendedSid ? 'is-recommended' : ''}
                aria-label={`${student.number}. ${student.name}${student.sid === recommendedSid ? ' (추천)' : ''}`}
                onClick={() => pickStudent(current, student.sid, student.sid === clueSid ? 'clue' : student.sid === recommendedSid ? 'recommended' : 'chip')}
              >
                {student.number}. {student.name}
                {student.sid === recommendedSid && <em>추천</em>}
              </button>
            ))}
          </div>
          <div className="class-roster-card-actions">
            {fillPlan.verified.length > 0 && (
              <button type="button" onClick={() => openFillPreview('verified')}>
                {`순서가 맞는 ${fillPlan.verified.length}명 한 번에 연결하기`}
              </button>
            )}
            {fillPlan.verified.length === 0 && fillPlan.anchorCount > 0 && unmatched.length >= 3
              && current.index !== unmatched[unmatched.length - 1].index && (
              <button type="button" className="is-quiet" onClick={() => setFocusIndex(unmatched[unmatched.length - 1].index)}>
                마지막 학생 먼저 고르기
              </button>
            )}
            {fillPlan.verified.length === 0 && fillPlan.guessed.length >= 2 && (
              <button type="button" className="is-quiet" onClick={() => openFillPreview('guessed')}>
                {`올린 순서대로 ${fillPlan.guessed.length}명 연결하기`}
              </button>
            )}
            <button type="button" className="is-quiet" onClick={() => keepAsIs(current)}>그대로 두기</button>
          </div>
        </div>
      )}
    </section>
  );
};

export default ClassRosterCard;
